import { useTranslation } from "react-i18next";
import { IconChevronRight } from "@tabler/icons-react";
import Loader from "../Loader";
import { useApiQuery } from "../../services/api";
import { cn } from "../../lib/utils";

/**
 * The partners whose privacy policy the reader can open: those of the rooms
 * they joined, when one of the rooms gives a link.
 */
type PartnerPolicies = {
  organisationIds: ReadonlySet<string>;
  onOpen: (organisationId: string) => void;
};

/**
 * The partners of Juisci: every organisation that supports a room. The
 * objection screen and the "Partner sharing" settings link to it, and the
 * Partners tab of the privacy policy shows it (Figma O4). A desktop shows the
 * logos bare, without row separators, under a hairline (Figma D3,
 * 5580:171302). A partner with an openable privacy policy is a button.
 */
export function PartnersList({
  layout,
  policies,
}: {
  layout: "mobile" | "desktop";
  policies: PartnerPolicies | null;
}): React.JSX.Element {
  const isDesktop = layout === "desktop";
  const { t } = useTranslation();
  const partnersQuery = useApiQuery("/webapp/private/partners", {});

  if (partnersQuery.state === "pending")
    return (
      <div className="flex justify-center py-10">
        <Loader />
      </div>
    );
  if (partnersQuery.state === "error")
    return (
      <p className="m-0 font-dm-sans text-footnote text-ds-slate-600">
        {t("Something went wrong. Please try again.")}
      </p>
    );

  return (
    <div className="flex flex-col gap-4">
      <p
        className={cn(
          "m-0 font-dm-sans",
          isDesktop
            ? "border-b border-ds-slate-100 pb-2.5 text-sm text-ds-slate-700"
            : "text-footnote text-ds-slate-700"
        )}
      >
        {t(
          "Healthcare companies and medical societies that support a room. When you join one of their rooms, they receive your email address unless you object."
        )}
      </p>
      <ul className="m-0 flex list-none flex-col p-0">
        {partnersQuery.body.map((partner) => {
          const row = (
            <>
              <span
                className={cn(
                  "flex shrink-0 items-center justify-center overflow-hidden",
                  isDesktop ? "size-8" : "size-9.5 rounded-lg bg-ds-moss-50 p-1"
                )}
              >
                <img
                  src={partner.logoUrl}
                  alt=""
                  className="max-h-full max-w-full object-contain"
                />
              </span>
              <span className="min-w-0 flex-1 text-left font-dm-sans text-ui-body font-medium text-ds-slate-900">
                {partner.name}
              </span>
            </>
          );
          const rowClassName = cn(
            "flex items-center",
            isDesktop
              ? "gap-3.5 py-2"
              : "gap-3 border-b border-ds-slate-100 py-3"
          );
          return (
            <li key={partner.organisationId}>
              {policies !== null &&
              policies.organisationIds.has(partner.organisationId) ? (
                <button
                  type="button"
                  className={cn(
                    rowClassName,
                    "w-full cursor-pointer border-none bg-transparent px-0"
                  )}
                  onClick={() => {
                    policies.onOpen(partner.organisationId);
                  }}
                >
                  {row}
                  <IconChevronRight
                    stroke={1.5}
                    className="size-4 shrink-0 text-ds-slate-500"
                  />
                </button>
              ) : (
                <div className={rowClassName}>{row}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
